import { Component, Input, signal, inject, computed, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { AreaMarketGenerationProgressService, AreaMarketGenerationProgress, AreaMarketGenerationStatus } from '@newroom-connect/library/services';
import { fadeInOutAnimation } from '@newroom-connect/library/animations';
import { Level } from '@newroom-connect/library/enums';

import { CollapsibleComponent } from '../../collapsible/collapsible.component';
import { IconComponent, IconFillColor } from '../../icon/icon.component';
import { LoadingSpinnerComponent } from '../../loading/loading-spinner/loading-spinner.component';
import { NoteComponent, NoteMessage } from '../../note/note.component';

interface StatusDisplayInfo {
  icon: string;
  color: IconFillColor;
  bgClass: string;
  textClass: string;
  badgeClass: string;
  showSpinner: boolean;
  stepLabel: string;
}

interface GenerationWithDisplayInfo extends AreaMarketGenerationProgress {
  displayInfo: StatusDisplayInfo;
  isDismissible: boolean;
  showCurrentWork: boolean;
  currentWorkDescription: string | null;
  areaProgressPercentage: number;
  elementProgressPercentage: number;
}

@Component({
  selector: 'nrc-area-market-generation-status',
  standalone: true,
  imports: [
    CommonModule,
    CollapsibleComponent,
    IconComponent,
    LoadingSpinnerComponent,
    NoteComponent
  ],
  templateUrl: './area-market-generation-status.component.html',
  styleUrls: ['./area-market-generation-status.component.scss'],
  animations: [fadeInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AreaMarketGenerationStatusComponent implements OnInit, OnDestroy {
  // Services
  private readonly areaMarketGenerationProgressService = inject(AreaMarketGenerationProgressService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Inputs
  @Input() public projectId!: string;

  // State
  public readonly isCollapsed = signal(false);
  public readonly activeGenerations = signal<AreaMarketGenerationProgress[]>([]);

  // Enums for template
  public readonly areaMarketGenerationStatus = AreaMarketGenerationStatus;

  // Subscriptions
  private subscription?: Subscription;

  // Computed signals for display logic
  public readonly hasActiveGenerations = computed(() => this.activeGenerations().length > 0);

  public readonly noteMessage = computed((): NoteMessage => ({
    message: 'Please do not update any area until the process is done to avoid data loss. Market area generation may take several minutes to complete.',
    level: Level.WARN,
    icon: 'info'
  }));

  public readonly generationsWithDisplayInfo = computed((): GenerationWithDisplayInfo[] => {
    const generations = this.activeGenerations();

    return generations.map(generation => {
      const displayInfo = this.getStatusDisplayInfo(generation);
      const isDismissible = generation.status === AreaMarketGenerationStatus.COMPLETED || generation.status === AreaMarketGenerationStatus.ERROR;

      // Calculate progress percentages
      const areaProgressPercentage = generation.totalAreas > 0
        ? Math.round((generation.processedAreas / generation.totalAreas) * 100)
        : 0;

      const elementProgressPercentage = generation.totalElements > 0
        ? Math.round((generation.processedElements / generation.totalElements) * 100)
        : 0;

      // Generate current work description
      const showCurrentWork = !!(generation.currentAreaTitle || generation.currentElementName || generation.fileTranslationTitle);
      const currentWorkDescription = this.getCurrentWorkDescription(generation);

      return {
        ...generation,
        displayInfo,
        isDismissible,
        showCurrentWork,
        currentWorkDescription,
        areaProgressPercentage,
        elementProgressPercentage
      };
    });
  });

  public ngOnInit(): void {
    // Subscribe to progress updates
    this.subscription = this.areaMarketGenerationProgressService.generationProgress.subscribe(
      (progress) => {
        if (progress) {
          this.activeGenerations.set(progress);
        } else {
          this.activeGenerations.set([]);
        }

        // Manually trigger change detection for OnPush strategy
        this.cdr.detectChanges();
      }
    );
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    // Stop all SSE connections
    const generations = this.activeGenerations();

    generations.forEach(generation => {
      this.areaMarketGenerationProgressService.stopWatching(generation.projectId);
    });
  }

  /**
   * Get complete display information for a generation status.
   *
   * @param generation The generation progress object.
   *
   * @returns Complete display information.
   */
  private getStatusDisplayInfo(generation: AreaMarketGenerationProgress): StatusDisplayInfo {
    const status = generation.status;

    switch (status) {
      case AreaMarketGenerationStatus.PENDING:
        return {
          icon: 'time',
          color: 'warning',
          bgClass: 'bg-yellow-500/20',
          textClass: 'text-yellow-400',
          badgeClass: 'text-yellow-500 bg-yellow-500/10',
          showSpinner: false,
          stepLabel: 'Initializing...'
        };

      case AreaMarketGenerationStatus.LOADING_DATA:
        return {
          icon: 'folder',
          color: 'cyan',
          bgClass: 'bg-blue-500/20',
          textClass: 'text-blue-400',
          badgeClass: 'text-blue-500 bg-blue-500/10',
          showSpinner: true,
          stepLabel: 'Preparing Data'
        };

      case AreaMarketGenerationStatus.CREATING_AREAS:
        return {
          icon: 'plus',
          color: 'cyan',
          bgClass: 'bg-blue-500/20',
          textClass: 'text-blue-400',
          badgeClass: 'text-blue-500 bg-blue-500/10',
          showSpinner: true,
          stepLabel: 'Creating Areas'
        };

      case AreaMarketGenerationStatus.CREATING_ELEMENTS:
        return {
          icon: 'layer-add',
          color: 'cyan',
          bgClass: 'bg-purple-500/20',
          textClass: 'text-purple-400',
          badgeClass: 'text-purple-500 bg-purple-500/10',
          showSpinner: true,
          stepLabel: 'Creating Elements'
        };

      case AreaMarketGenerationStatus.TRANSLATING_FILES:
        return {
          icon: 'translate',
          color: 'cyan',
          bgClass: 'bg-indigo-500/20',
          textClass: 'text-indigo-400',
          badgeClass: 'text-indigo-500 bg-indigo-500/10',
          showSpinner: true,
          stepLabel: 'Translating Content'
        };

      case AreaMarketGenerationStatus.COMPLETED:
        return {
          icon: 'multi-check',
          color: 'success',
          bgClass: 'bg-green-500/20',
          textClass: 'text-green-400',
          badgeClass: 'text-green-500 bg-green-500/10',
          showSpinner: false,
          stepLabel: 'Completed'
        };

      case AreaMarketGenerationStatus.ERROR:
        return {
          icon: 'triangle-warning',
          color: 'error',
          bgClass: 'bg-red-500/20',
          textClass: 'text-red-400',
          badgeClass: 'text-red-500 bg-red-500/10',
          showSpinner: false,
          stepLabel: 'Failed'
        };

      default:
        return {
          icon: 'gear',
          color: 'cyan',
          bgClass: 'bg-gray-500/20',
          textClass: 'text-gray-400',
          badgeClass: 'text-gray-500 bg-gray-500/10',
          showSpinner: false,
          stepLabel: 'Processing...'
        };
    }
  }

  /**
   * Generate current work description based on the generation state.
   *
   * @param generation The generation progress object.
   *
   * @returns Description of current work or null.
   */
  private getCurrentWorkDescription(generation: AreaMarketGenerationProgress): string | null {
    if (generation.fileTranslationTitle) {
      return `Translating: ${generation.fileTranslationTitle}`;
    }

    if (generation.currentElementName && generation.currentAreaTitle) {
      return `Creating "${generation.currentElementName}" in "${generation.currentAreaTitle}"`;
    }

    if (generation.currentAreaTitle) {
      return `Working on area: ${generation.currentAreaTitle}`;
    }

    if (generation.currentElementName) {
      return `Creating element: ${generation.currentElementName}`;
    }

    return null;
  }

  /**
   * Clear a specific generation progress.
   *
   * @param jobId The job ID to clear.
   */
  public clearGeneration(jobId: string): void {
    // Find the generation by jobId to get the correct id
    const generation = this.activeGenerations().find(g => g.jobId === jobId);

    if (generation) {
      this.areaMarketGenerationProgressService.removeProgress(generation.id);
    } else {
      // Fallback: try removing by jobId in case id and jobId are the same
      this.areaMarketGenerationProgressService.removeProgress(jobId);
    }
  }

  /**
   * Clear all generation progress.
   */
  public clearAllGenerations(): void {
    this.areaMarketGenerationProgressService.clearAllProgress();
  }
}
